import { StrictMode, Component, type ReactNode, useEffect } from 'react';
import ReactDOM from 'react-dom/client';

import { queryClient } from '@/lib/query-client';
import { AuthProvider, useAuth } from '@/context/auth-context';
import { DarkModeProvider } from '@/context/dark-mode-context';
import { QueryClientProvider } from '@tanstack/react-query';
import { ErrorBoundary as SentryErrorBoundary } from '@sentry/react';

import '@/styles/globals.css';
import { routeTree } from '@/routeTree.gen';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { APIProvider } from '@vis.gl/react-google-maps';
import { CookieConsentManager } from '@/components/shared/cookie-consent-manager';
import { FCMBootstrapper } from '@/components/shared/fcm-bootstrapper';
import { isWebview } from '@/utils/webview-detection';
import { initSentry } from '@/lib/sentry';
import { Track, type TrackingContext } from '@/lib/tracker';
import API from '@/lib/axios';
import {
  userOnboardingCompletionStore,
  type ApplicationCompletion,
} from '@/stores/user-onboarding-completion';
import { computeOnboardingCurrentStep } from '@/utils/application-completion';
import { hasTokenCookie } from '@/lib/cookies';
import { useUserDetailStore } from '@/stores/user-detail-store';
import { useGetUserDetailQuery } from '@/hooks/queries/use-get-user-detail.query';

// Initialize Sentry before anything else
initSentry();

const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  scrollRestoration: true,
  context: { queryClient, auth: undefined! },
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

export function InnerApp() {
  const auth = useAuth();

  return <RouterProvider router={router} context={{ auth }} />;
}

function UserDetailBootstrapper() {
  const auth = useAuth();
  const setUser = useUserDetailStore(s => s.setUser);

  const userid = auth.user?.userid ? String(auth.user.userid) : '';

  const { data } = useGetUserDetailQuery(userid, {
    enabled: !!userid,
  });

  useEffect(() => {
    if (data?.details) {
      setUser(data.details);
    }
  }, [data, setUser]);

  return null;
}

function OnboardingCompletionBootstrapper() {
  const setApplicationCompletion = userOnboardingCompletionStore(
    s => s.setApplicationCompletion
  );
  const applicationCompletion = userOnboardingCompletionStore(
    s => s.applicationCompletion
  );

  useEffect(() => {
    const fetchApplicationCompletion = async () => {
      try {
        // Only fetch once per FE load and only if authenticated
        if (!hasTokenCookie()) {
          return;
        }

        const response = await API.get('/users/application-completion');
        const data = response.data?.data as ApplicationCompletion | undefined;
        if (data) {
          const currentStep = computeOnboardingCurrentStep(
            data,
            applicationCompletion.currentStep
          );

          setApplicationCompletion({ ...data, currentStep });
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(
          'Error fetching application completion on bootstrap:',
          error
        );
      }
    };

    void fetchApplicationCompletion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}

function ErrorFallback({
  error,
  resetError,
  componentStack,
  eventId,
}: {
  error: unknown;
  resetError: () => void;
  componentStack: string;
  eventId?: string;
}) {
  const errorMessage = error instanceof Error ? error.message : String(error);
  const errorStack = error instanceof Error ? error.stack : undefined;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '2rem',
        textAlign: 'center',
      }}
    >
      <h1 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>
        ⚠️ Something went wrong
      </h1>
      <p style={{ color: '#666', marginBottom: '1.5rem' }}>
        {import.meta.env.DEV
          ? 'An error occurred. Check the details below.'
          : "We're sorry, but something unexpected happened. Our team has been notified."}
      </p>
      <button
        type="button"
        onClick={resetError}
        style={{
          padding: '0.75rem 1.5rem',
          backgroundColor: '#007bff',
          color: 'white',
          border: 'none',
          borderRadius: '0.25rem',
          cursor: 'pointer',
          fontSize: '1rem',
        }}
      >
        Try again
      </button>
      {import.meta.env.DEV && (
        <details
          style={{ marginTop: '2rem', textAlign: 'left', maxWidth: '600px' }}
        >
          <summary style={{ cursor: 'pointer', marginBottom: '0.5rem' }}>
            Error details
          </summary>
          <pre
            style={{
              padding: '1rem',
              backgroundColor: '#f5f5f5',
              borderRadius: '0.25rem',
              overflow: 'auto',
              fontSize: '0.875rem',
            }}
          >
            {errorMessage}
            {errorStack && (
              <>
                {'\n\n'}
                {errorStack}
              </>
            )}
            {componentStack && (
              <>
                {'\n\n'}
                Component Stack:
                {componentStack}
              </>
            )}
            {eventId && (
              <>
                {'\n\n'}
                Event ID: {eventId}
              </>
            )}
          </pre>
        </details>
      )}
    </div>
  );
}

// Simple React Error Boundary for development mode
class DevErrorBoundary extends Component<
  { children: ReactNode; fallback: typeof ErrorFallback },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: ReactNode; fallback: typeof ErrorFallback }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: { componentStack: string }) {
    // eslint-disable-next-line no-console
    console.error('Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <this.props.fallback
          error={this.state.error}
          resetError={() => this.setState({ hasError: false, error: null })}
          componentStack=""
        />
      );
    }

    return this.props.children;
  }
}

// Component that wraps the app with all necessary providers
function AppWithProviders({ children }: { children: ReactNode }) {
  return (
    <QueryClientProvider client={queryClient}>
      <DarkModeProvider>
        <AuthProvider>
          <APIProvider
            apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY!}
            libraries={['places']}
          >
            {children}
          </APIProvider>
        </AuthProvider>
      </DarkModeProvider>
    </QueryClientProvider>
  );
}

// Wrapper component to provide auth context for tracking
function AppWithTracking() {
  const auth = useAuth();

  // Generate initial context for tracker
  const initialContext: TrackingContext = {
    userId: auth.user?.userid ? String(auth.user.userid) : undefined,
    userRole: auth.user?.role as 'customer' | 'cleaner' | undefined,
    hasConsent: true, // TODO: Implement consent management
  };

  return (
    <Track.Provider initialContext={initialContext}>
      <OnboardingCompletionBootstrapper />
      <UserDetailBootstrapper />
      <InnerApp />
      {!isWebview() && <CookieConsentManager />}
      {!isWebview() && <FCMBootstrapper />}
    </Track.Provider>
  );
}

const rootElement = document.getElementById('root')!;
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);

  const AppContent = (
    <AppWithProviders>
      <AppWithTracking />
    </AppWithProviders>
  );

  root.render(
    <StrictMode>
      {import.meta.env.DEV ? (
        <DevErrorBoundary fallback={ErrorFallback}>
          {AppContent}
        </DevErrorBoundary>
      ) : (
        <SentryErrorBoundary fallback={ErrorFallback} showDialog>
          {AppContent}
        </SentryErrorBoundary>
      )}
    </StrictMode>
  );
}
